import { ActivatedRouteSnapshot, UrlSegment } from "@angular/router";

export interface IOpenOutletsInfo {
  perspectiveId: string,
  outlets: {outlet: string, id: string}[]
}

interface IRoutingSegment {
  outlet: string,
  url: UrlSegment[];
}

const ERR_MSG = 'URL structure no longer matches expected - can not reliably recognize perspective and active page';
/**
 * The structure of URL contains information on both perspective and currently
 * active secondary outlets. We need to parse that info into usable format.
 */
export class LocationParser {

  urlStructure: Array<IRoutingSegment[]> = [];

  start(route: ActivatedRouteSnapshot): IOpenOutletsInfo {
    this._parseRouteSnapshot(route, []);
    const result: IOpenOutletsInfo = {
      perspectiveId: '',
      outlets: []
    }
    this._parseUrlSegments(result);
    return result;
  }

  _parseUrlSegments(result: IOpenOutletsInfo) {
    if (this.urlStructure.length === 0) {
      // this signals a severe error - probably URL structure has changed, and you need to rewrite this method
      throw Error(ERR_MSG);
    }
    // some assumptions are deployed here - about overall URL structure. If that URL structure is changed,
    // then you need to rewrite this method. In particular, a typical URL will look like this:
    // /perspective/dfvhu/(wizard:active_test//page:yuidsa)
    // There are 3 outlets here:
    // primary (perspective/dfvhu), secondary A (wizard:active_test), secondary B (page:yuidsa)
    // We assume that primary outlet is the same for any URL structure.
    const firstPath = this.urlStructure[0][0];
    if (firstPath.outlet === 'primary' && firstPath.url.length === 2) {
      result.perspectiveId = firstPath.url[1].path;
    }
    else {
      throw Error(ERR_MSG);
    }
    for (const paths of this.urlStructure) {
      // some outlet navigation paths will have sub-paths. example:
      // http://localhost:4200/#/perspective/dfvhu/(dashboard:trace-extraction/jobs//page:yuidsa)#dashboard
      // in this example, dashboard outlet urlStructure will look like this:
      // [0]th - primary outlet with perspective info(perspective/dfvhu)
      // [1]st - named outlet dashboard with (trace-extraction)
      // [2]nd - PRIMARY (!) outlet with path 'jobs'. Why primary? because the trace-extraction page will have nested <route>
      //         without name. For our purposes (to find out what are top named outlets) this knowledge is not relevant,
      //         thus we just 'live' with that knowledge.
      if (paths.length < 2) {
        throw Error(ERR_MSG);
      }
      // the last member on the path is either wizard or page
      const detailsOutlet = paths[1];
      // eg: 'outlet:page, url: [UrlSegment(path=somepageId)]'
      // result.outlets[detailsOutlet.outlet] = detailsOutlet.url[0].path;
      result.outlets.push({outlet: detailsOutlet.outlet, id: detailsOutlet.url[0].path});
    }
  }

  _parseRouteSnapshot(route: ActivatedRouteSnapshot, currentSegments: IRoutingSegment[]) {
    if (route.url.length !== 0) {
      currentSegments.push({outlet: route.outlet, url: route.url});
    }
    if (route.children.length === 0) {
      this.urlStructure.push(currentSegments);
    }
    else {
      for (const childRoute of route.children) {
        this._parseRouteSnapshot(childRoute, currentSegments.slice(0));
      }
    }

  }
}
